import { useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { ArrowLeft, ClipboardCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Notify } from "@/components/ui/notify";
import { PageHeader } from "@/components/PageHeader";
import { useRealtimeRooms } from "@/lib/use-realtime-rooms";
import type { AppliedScenario } from "@/types/applied-scenario.types";
import { AppliedScenarioCard } from "./AppliedScenarioCard";
import { CancelAppliedScenarioDialog } from "./CancelAppliedScenarioDialog";
import { SettlementSection } from "./SettlementSection";
import { AdherenceSection } from "./AdherenceSection";
import { ClassificationSection } from "./ClassificationSection";
import { useAppliedScenarios, useSettleAppliedScenario } from "./hooks/useAppliedScenario";

// Os COMPROMISSOS de uma linha: o que foi decidido, para qual janela, e o
// que aconteceu depois. Orquestrador puro (hooks + composição); tudo que desenha mora ao lado.

export function AppliedScenariosPage() {
  const { lineId = "" } = useParams();
  const navigate = useNavigate();
  // ⚠️ SEM isto o aviso de aderência não chega: o worker publica `applied-scenario:adherence` no
  // room `line:<id>`, e o servidor só re-emite para rooms — um socket que não entrou em nenhum não
  // recebe evento algum. É esta tela que desenha o veredito, então é ela que precisa do room.
  // (`applied-scenario:settled` vai para `org:<id>`, em que todo socket autenticado já está.)
  useRealtimeRooms(useMemo(() => (lineId ? [lineId] : []), [lineId]));
  const query = useAppliedScenarios(lineId);
  const settle = useSettleAppliedScenario();
  const [cancelTarget, setCancelTarget] = useState<AppliedScenario | null>(null);
  // Spec 077 (FR-044) — as duas filas do ciclo fechado linkam PARA UM COMPROMISSO, e a exigência é
  // "com a apuração completa à vista". Sem semear o card aberto a partir de `?compromisso=<id>`,
  // quem clica cai numa lista de cards colapsados idênticos e tem de adivinhar a janela certa —
  // que é literalmente "decidir sem o contexto" que a regra existe para impedir.
  const [params] = useSearchParams();
  const [expandedId, setExpandedId] = useState<string | null>(params.get("compromisso"));

  // Apurar é IRREVERSÍVEL e terminal: congela as três colunas e o índice de aderência para sempre
  // (`SETTLED` recusa reapuração com 409). Um clique errado num card da lista fechava a conclusão
  // de uma decisão com o dado que existisse naquele instante, sem desfazer em lugar nenhum.
  async function confirmAndSettle(s: AppliedScenario) {
    const ok = await Notify.confirm({
      variant: "warning",
      title: "Apurar este compromisso agora?",
      description:
        "A apuração é única: previsto, realizado e base ficam gravados, e a aderência congela no valor deste instante. Depois disso o compromisso não é mais avaliado e não há como reapurar.",
      confirmLabel: "Apurar resultado",
      cancelLabel: "Voltar",
    });
    if (!ok) return;
    settle.mutate({ id: s.id, lineId: s.lineId }, { onSuccess: () => setExpandedId(s.id) });
  }

  // Falha de leitura NÃO pode virar "nenhum compromisso": um histórico de decisões vazio por erro
  // de rede é a mentira mais cara que esta tela pode contar.
  if (query.isError) {
    return (
      <Notify.QueryError
        error={query.error}
        onRetry={() => void query.refetch()}
        title="Não foi possível carregar os compromissos desta linha"
      />
    );
  }

  const scenarios = query.data;

  return (
    <section>
      <PageHeader
        title={
          scenarios?.[0]?.lineName ? `Compromissos · ${scenarios[0].lineName}` : "Compromissos"
        }
        subtitle="Cenários aplicados a esta linha: a decisão, a janela e o que a apuração encontrou."
        actions={
          <Button variant="outline" onClick={() => navigate("/simulations")}>
            <ArrowLeft className="h-4 w-4" />
            Voltar
          </Button>
        }
      />

      {query.isPending ? (
        <p className="text-sm text-muted-foreground">Carregando…</p>
      ) : scenarios && scenarios.length > 0 ? (
        <div className="space-y-4">
          {scenarios.map((scenario) => (
            <AppliedScenarioCard
              key={scenario.id}
              scenario={scenario}
              isSettling={settle.isPending && settle.variables?.id === scenario.id}
              expanded={expandedId === scenario.id}
              onToggleExpand={() =>
                setExpandedId((current) => (current === scenario.id ? null : scenario.id))
              }
              onCancel={setCancelTarget}
              onSettle={(s) => void confirmAndSettle(s)}
            >
              {/* Apurado ⇒ as três colunas (a aderência congelada viaja dentro). Em vigência ⇒ só a
                  aderência, RECALCULADA: é o desvio que ainda dá para corrigir. */}
              {scenario.state === "SETTLED" ? (
                <div className="space-y-4">
                  <SettlementSection scenario={scenario} />
                  {/* Spec 077 — a apuração deixa de ser o fim da jornada: ela responde QUANTO
                      divergiu, e a seção abaixo responde POR QUÊ. */}
                  <ClassificationSection scenario={scenario} />
                </div>
              ) : (
                <AdherenceSection scenario={scenario} />
              )}
            </AppliedScenarioCard>
          ))}
        </div>
      ) : (
        <Notify.Empty
          icon={<ClipboardCheck className="h-5 w-5" />}
          title="Nenhum cenário aplicado nesta linha"
          description="Quando uma recomendação virar compromisso, ela aparece aqui com a janela, o responsável e — depois da vigência — a apuração do que realmente aconteceu."
          action={
            <Button variant="outline" onClick={() => navigate("/simulations")}>
              Ir para as simulações
            </Button>
          }
        />
      )}

      <CancelAppliedScenarioDialog
        scenario={cancelTarget}
        onOpenChange={(open) => !open && setCancelTarget(null)}
      />
    </section>
  );
}
